// useImageResize.ts
import { useState, useCallback } from 'react';
import Compressor from 'compressorjs';

interface ResizeOptions {
  maxWidth?: number;
  maxHeight?: number;
  aspectRatio?: number;
  quality?: number;
  mimeType?: string;
}

interface ResizeResult {
  file: File;
  preview: string;
  width: number;
  height: number;
}

export function useImageResize(options: ResizeOptions = {}) {
  const [isProcessing, setIsProcessing] = useState(false);
  
  const resizeImage = useCallback(async (file: File): Promise<ResizeResult> => {
    const { 
      maxWidth = 1200, 
      maxHeight = 1200, 
      aspectRatio,
      quality = 0.8,
      mimeType = 'image/jpeg'
    } = options;

    return new Promise((resolve, reject) => {
      setIsProcessing(true);

      new Compressor(file, {
        maxWidth,
        maxHeight,
        quality,
        mimeType,
        strict: false,
        checkOrientation: true,
        success: async (result: Blob) => {
          try {
            // Create dimensions that maintain aspect ratio if specified
            const image = await createImageBitmap(result);
            let width = image.width;
            let height = image.height;

            if (aspectRatio) {
              const targetHeight = width / aspectRatio;
              if (targetHeight < height) {
                height = targetHeight;
              } else {
                width = height * aspectRatio;
              }
            }

            // Convert blob to file
            const resizedFile = new File(
              [result], 
              file.name, 
              { type: mimeType }
            );

            // Generate preview URL
            const preview = URL.createObjectURL(result);

            resolve({
              file: resizedFile,
              preview,
              width,
              height
            });
          } catch (err) {
            reject(err);
          } finally {
            setIsProcessing(false);
          }
        },
        error: (err) => {
          setIsProcessing(false);
          reject(err);
        },
      });
    });
  }, [options]);

  return {
    resizeImage,
    isProcessing
  };
}
